"use client";

import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTableToolbar } from "./data-table-toolbox";
import { DataTablePagination } from "./data-table-pagination";
import { useDefineStore } from "@/store/define";
import { useShippingModeStore } from "@/store/shipping-modes";
import { usePalletStore } from "@/store/pallets";
import { Button } from "@/components/ui/button";
import { Download, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { useDownloadProjectMutation, useUploadProjectMutation } from "@/redux/slices/offline/offlineApiSlice";
import { UploadModal } from "./upload-modal";
import { db } from '@/services/Database';
import { showErrorToast, showSuccessToast } from "@/components/toast/toast";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isOffline?: boolean;
  newOffline?: boolean;
  isDistribution?: boolean;
  loadOfflineProjects?: () => void;
  serverPagination?: boolean;
  totalPages?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onMockDelete?: (project: TData) => void;
  onDeleteProject?: (project: TData) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isDistribution = false,
  newOffline = false,
  isOffline = false,
  loadOfflineProjects,
  serverPagination = false,
  totalPages,
  currentPage,
  onPageChange,
  searchValue,
  onSearchChange,
  onMockDelete,
  onDeleteProject,
}: DataTableProps<TData, TValue>) {
  const { clearShippingModes } = useShippingModeStore();
  const { clearPallets } = usePalletStore();
  const { clearDefineData } = useDefineStore();

  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [selectedRows, setSelectedRows] = useState<Record<string | number, boolean>>({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  const [downloadProject] = useDownloadProjectMutation();
  const [uploadProject] = useUploadProjectMutation();

  // Only clear data in online mode
  React.useEffect(() => {
    if (!isOffline) {
      clearDefineData();
      clearPallets();
      clearShippingModes();
    }
  }, [isOffline, clearDefineData, clearPallets, clearShippingModes]);

  // Transform data for offline mode if needed
  const transformedData = React.useMemo(() => {
    if (isOffline) {
      return data.map((item: any) => ({
        id: item.id || item.projectId,
        projectName: item.projectDetails?.projectName || item.projectName || 'N/A',
        createdAt: item.createdAt || new Date().toISOString(),
        creator: {
          name: item.createdBy || 'Offline User'
        },
        overallDecision: item.overallDecision || 'pending',
        status: 'Offline',
        // Preserve original data
        ...item
      }));
    }
    return data;
  }, [data, isOffline]);

  const table = useReactTable({
    data: transformedData,
    columns: isOffline ? columns.filter(col => col.id !== 'select') : columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: !isOffline,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    meta: {
      onMockDelete,
      onDeleteProject,
    },
  });

  // Handle downloading selected projects
  const handleDownload = async () => {
    try {
      const selectedProjects = table
        .getSelectedRowModel()
        .rows.map(row => row.original);

      if (selectedProjects.length === 0) {
        showErrorToast("Please select at least one project to download");
        return;
      }

      for (const project of selectedProjects) {
        const response = await downloadProject(project.id).unwrap();

        await db.projects.put({
          ...response.data,
          offlineMetadata: {
            downloadedAt: new Date().toISOString(),
            onlineId: project.id,
            status: 'downloaded',
            syncStatus: 'pending'
          }
        });
      }

      showSuccessToast(`${selectedProjects.length} projects downloaded successfully`);
      table.resetRowSelection();
    } catch (error) {
      console.error('Error downloading projects:', error);
      showErrorToast("Failed to download projects");
    }
  };

  // Handle opening upload modal
  const handleUploadClick = (project: any) => {
    setSelectedProject(project);
    setIsUploadModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <DataTableToolbar
          isDistribution={isDistribution}
          table={table}
          isOffline={isOffline}
          serverPagination={serverPagination}
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={onPageChange}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
        />
        {!isOffline && table.getSelectedRowModel().rows.length > 0 && (
          <Button onClick={handleDownload} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Download Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </div>

      <div className="rounded-none">
        <Table>
          <TableHeader className="bg-light-blue">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-light-blue">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="font-bold text-black x-space"
                    colSpan={header.colSpan}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="p-4">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        project={selectedProject}
      />

      {/* Server-side pagination controls at bottom */}
      {serverPagination && totalPages && totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 py-4">
          <Button
            variant="ghost"
            className="p-0 text-blue"
            onClick={() => onPageChange && onPageChange(Math.max((currentPage || 1) - 1, 1))}
            disabled={(currentPage || 1) === 1}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          {Array.from({ length: totalPages }).map((_, idx) => {
            const pageNum = idx + 1;
            const isActive = pageNum === currentPage;
            // Show limited buttons when many pages
            if (totalPages > 7) {
              const range = [1, totalPages, (currentPage || 1) - 1, currentPage, (currentPage || 1) + 1];
              if (!range.includes(pageNum)) {
                if (pageNum === 2 || pageNum === totalPages - 1) {
                  return <span key={pageNum} className="px-1">...</span>;
                }
                return null;
              }
            }
            return (
              <Button
                key={pageNum}
                variant={isActive ? 'default' : 'ghost'}
                size="sm"
                className={isActive ? 'bg-blue text-white' : 'text-blue'}
                onClick={() => onPageChange && onPageChange(pageNum)}
              >
                {pageNum}
              </Button>
            );
          })}
          <Button
            variant="ghost"
            className="p-0 text-blue"
            onClick={() => onPageChange && onPageChange(Math.min((currentPage || 1) + 1, totalPages))}
            disabled={(currentPage || 1) === totalPages}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
